import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogManagement = () => {
  const { getAuthHeader } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: '',
    image: '',
    read_time: '5 min read',
    is_published: false
  });

  const loadPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/blog`, {
        headers: getAuthHeader()
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load blog posts');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: '',
      category: '',
      image: '',
      read_time: '5 min read',
      is_published: false
    });
    setEditingPost(null);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      category: post.category,
      image: post.image || '',
      read_time: post.readTime || post.read_time || '5 min read',
      is_published: post.isPublished ?? post.is_published ?? false
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API_URL}/admin/blog/${editingPost.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Blog post updated');
      } else {
        await axios.post(`${API_URL}/admin/blog`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
        });
        toast.success('Blog post created');
      }
      setIsDialogOpen(false);
      resetForm();
      loadPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API_URL}/admin/blog/${postId}`, {
        headers: getAuthHeader()
      });
      toast.success('Blog post deleted');
      loadPosts();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API_URL}/admin/blog/${post.id}`, {
        is_published: !(post.isPublished ?? post.is_published)
      }, { headers: getAuthHeader() });
      toast.success(post.isPublished || post.is_published ? 'Post unpublished' : 'Post published');
      loadPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const categories = ['Parenting', 'Education', 'Reading Tips', 'Book Reviews', 'Activities', 'News'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Blog Management</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-blog-post-btn">
              <Plus className="mr-2 h-4 w-4" /> New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit Blog Post' : 'New Blog Post'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="blog-form">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="blog-title-input"
                />
              </div>
              <div>
                <Label htmlFor="author">Author</Label>
                <Input
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  required
                  data-testid="blog-author-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  data-testid="blog-category-select"
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="excerpt">Excerpt (short summary)</Label>
                <Textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  rows={2}
                  required
                  data-testid="blog-excerpt-input"
                />
              </div>
              <div>
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={10}
                  required
                  placeholder="Write your blog post content here..."
                  data-testid="blog-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Blog Image</Label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploadingImage(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await axios.post(`${API_URL}/admin/blog/upload-image`, fd, {
                          headers: getAuthHeader()
                        });
                        setFormData(prev => ({ ...prev, image: res.data.imageUrl }));
                        toast.success('Image uploaded');
                      } catch (err) {
                        toast.error('Failed to upload image');
                      } finally {
                        setUploadingImage(false);
                      }
                    }}
                    data-testid="blog-image-file-input"
                  />
                  {formData.image ? (
                    <div className="mt-1 relative">
                      <img
                        src={formData.image.startsWith('http') ? formData.image : `${process.env.REACT_APP_BACKEND_URL}${formData.image}`}
                        alt="Blog preview"
                        className="w-full h-24 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? 'Uploading...' : 'Change Image'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mt-1 h-24 border-dashed"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      data-testid="blog-upload-image-btn"
                    >
                      {uploadingImage ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                          Uploading...
                        </span>
                      ) : (
                        <span className="flex flex-col items-center gap-1 text-gray-500">
                          <Upload className="h-5 w-5" />
                          <span className="text-xs">Upload Image</span>
                        </span>
                      )}
                    </Button>
                  )}
                </div>
                <div>
                  <Label htmlFor="read_time">Read Time</Label>
                  <Input
                    id="read_time"
                    value={formData.read_time}
                    onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                    placeholder="5 min read"
                    data-testid="blog-readtime-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="blog-publish-checkbox"
                />
                <Label htmlFor="is_published">Publish immediately</Label>
              </div>
              <Button type="submit" className="w-full" data-testid="blog-submit-btn">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {posts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            <p className="text-lg">No blog posts yet</p>
            <p className="text-sm mt-2">Click "New Post" to create your first blog post</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.id} data-testid={`blog-post-${post.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold text-gray-800 truncate">{post.title}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      (post.isPublished ?? post.is_published)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(post.isPublished ?? post.is_published) ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{post.excerpt}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    By {post.author} &middot; {post.readTime || post.read_time}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => togglePublish(post)}
                    title={(post.isPublished ?? post.is_published) ? 'Unpublish' : 'Publish'}
                    data-testid={`blog-toggle-${post.id}`}
                  >
                    {(post.isPublished ?? post.is_published)
                      ? <EyeOff className="h-4 w-4 text-yellow-600" />
                      : <Eye className="h-4 w-4 text-green-600" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(post)}
                    data-testid={`blog-edit-${post.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(post.id)}
                    data-testid={`blog-delete-${post.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
